/* eslint-disable import-x/order */
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * This App import should be kept as the first import in order to avoid css
 * source ordering issues.
 */
import { App } from './App';
import { ClientRouteProvider } from './static-context';
import { ContentSplitProvider } from './abTesting/ContentSplitProvider';

import {
  configureCoupons,
  PromotionCapture,
  PromotionProvider,
  DiscountType,
  isShadowing,
  isContextPrimary,
} from '@farmersdog/coupons';
import type { PromotionContextTreatment } from '@farmersdog/coupons';
import { getCookieDomain } from '@farmersdog/utils';
import config from './config';
import { FeatureName } from './abTesting/features';
import { useContentFeature } from './abTesting/useContentFeature';

configureCoupons({
  apiUrl: `${config.get('app.apiUrl')}/referrer`,
  cookieDomain: getCookieDomain(),
});

export function ClientApp() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ClientRouteProvider>
            <ContentSplitProvider>
              <PromotionProviderWrapper>
                <App />
              </PromotionProviderWrapper>
            </ContentSplitProvider>
          </ClientRouteProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

const defaultDiscount = {
  discountType: DiscountType.percentage,
  discountAmount: config.get('features.defaultDiscountPercentage'),
};

// TODO: Remove this after shadowing phase for promotion context is complete. This wrapper exists to gate the new system behind feature flag while both co-exist.
function PromotionProviderWrapper({ children }: { children: React.ReactNode }) {
  const { treatment: rawTreatment, isReady } = useContentFeature(
    FeatureName.MT_DISCOUNT_CONTEXT,
    {}
  );
  const treatment = rawTreatment as PromotionContextTreatment;
  const enabled = isShadowing(treatment) || isContextPrimary(treatment);

  return (
    <PromotionProvider defaultPromotion={defaultDiscount} enabled={enabled}>
      {isReady && <PromotionCapture treatment={treatment} />}
      {children}
    </PromotionProvider>
  );
}
